/* -------------------------- Design imports start -------------------------- */
import { IconButton, Menu } from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import LogTool from '../../logger/logTools'
/* ------------------------- Functional imports end ------------------------- */



type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  disabled?: boolean
  children: ReactNode
  onPrimaryClick?: (buttonEl: HTMLButtonElement | null, event: React.MouseEvent<HTMLButtonElement>) => void
}


/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function TableOptions(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    disabled = false,
    children,
    onPrimaryClick,
  } = props
  const log = new LogTool({context: 'TableOptions', enable: false, logLevel:'debug'})

  let menuAnchorElem: null | HTMLElement = null;
  /* --------------------------- Non state data end --------------------------- */


  /* ---------------------------- Data states start --------------------------- */
  const { t } = useTranslation(['common'])
  const anchorElem = useRef<null | HTMLButtonElement>(null)
  // const [menuAnchorElem, setMenuAnchorElem] = useState<null | HTMLElement>(null)
  /* ----------------------------- Data states end ---------------------------- */



  /* ------------------------ Callback functions start ------------------------ */
  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onPrimaryClick) {
      onPrimaryClick(anchorElem.current, event)
      return
    }
    // this will trigger a re-render of the parent component and therefore also
    // a re-render of this component. Pre-Render actions are run before the render
    // is complete, allowing us to control the MUI menu component using only the
    // open prop!
    setOpen(true)
  }

  const handleCloseMenu = () => {
    setOpen(false)
  }
  /* ------------------------- Callback functions end ------------------------- */



  /* ------------------------ Pre render actions start ------------------------ */
  // This pre-render action allows us to do two important things:
  // 1. The open state of the MoreOptionsMenu can be fully controlled from the parent component
  // 2. Whenever the parent component changes the open state the MoreOptionsMenu
  //    is able to react in the same render pass and does not need to trigger a second
  //    render to open the menu
  if(open) {
    menuAnchorElem = anchorElem.current
  } else {
    menuAnchorElem = null
  }
  /* ------------------------- Pre render actions end ------------------------- */


  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <React.Fragment>
      <IconButton
        id="moreOptionsButton"
        data-testid="more-options-button"
        aria-controls="more-options-menu"
        ref={anchorElem}
        onClick={handleOpenMenu}
        disabled={disabled}
      >
        <SettingsIcon />
      </IconButton>
      <Menu
        id="moreOptionsMenu"
        open={open}
        anchorEl={menuAnchorElem}
        onClose={handleCloseMenu}

        /* Transformation */
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {children}
      </Menu>
    </React.Fragment>
  )
}
